import { getThumbnailUrlWithToken } from '@/connection/mux';
import classNames from 'classnames';
import { ImgHTMLAttributes, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import classes from './video-thumbnail.module.css';
import VideoSvg from '@images/icons/icon-video-filled.svg';

// Mux streams with jwt privacy enabled still require jwt signatures for their thumbnails
// this will check if the image requires a jwt token and async update the src of the image
// element as soon as the jwt is loaded in. It also forces the image to be lazy loaded
export default function VideoThumbnail(
  props: ImgHTMLAttributes<HTMLImageElement> & {
    errorElement?: React.ReactNode;
    loadingElement?: React.ReactNode;
  }
) {
  const [src, setSrc] = useState('');
  const [ready, setReady] = useState(false);
  const [errored, setErrored] = useState(false);
  const onErrorRef = useRef(props.onError);
  const { src: inputSrc, ...rest } = props;
  useEffect(() => {
    onErrorRef.current = props.onError;
  }, [props.onError]);

  useEffect(() => {
    if (inputSrc) {
      getThumbnailUrlWithToken(inputSrc)
        .then((res: string | Error) => {
          if (res instanceof Error) {
            console.error('Error fetching thumbnail URL:', res);
            setErrored(true);
            return;
          }
          setSrc(res);
          setErrored(false);
        })
        .finally(() => setReady(true));
    }
  }, [inputSrc]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = useCallback((e: any) => {
    setErrored(true);
    if (onErrorRef.current) {
      onErrorRef.current(e);
    }
  }, []);

  if (!ready && props.loadingElement) {
    return <>{props.loadingElement}</>;
  }

  if (errored && props.errorElement) {
    return <>{props.errorElement}</>;
  }

  if (errored || !src) {
    return (
      <div
        className={classNames(classes.videoThumbError, props.className)}
        style={{ ...props.style, '--background-image': `url("${VideoSvg}")` } as CSSProperties}
      />
    );
  }

  return <img {...rest} src={src} loading="lazy" onError={ready ? handleError : undefined} />;
}
